/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import { isLegacyImageType, isLogoType } from 'DesignData/helpers/widgets';
import { createOptimizedPick, isPlainObject } from 'DesignData/lib/object';
import ImageSizeTypes from 'ContentUtils/constants/ImageSizeTypes';
const IMAGE_WIDGET_PARAMS = Object.freeze(['src', 'alt', 'height', 'width']);
const LOGO_IMAGE_WIDGET_PARAMS = Object.freeze([...IMAGE_WIDGET_PARAMS, 'override_inherited_src', 'suppress_company_name']);
const IMAGE_SLIDER_PARAMS_MAPPING = Object.freeze({
  alt_text: 'alt',
  img_src: 'src'
});
const IMAGE_SLIDER_LEGACY_PARAMS = Object.keys(IMAGE_SLIDER_PARAMS_MAPPING);
const FORM_WIDGET_PARAMS_MAPPING = Object.freeze({
  form_to_use: 'form_id',
  response_response_type: 'response_type',
  response_message: 'message',
  response_redirect_id: 'redirect_id',
  response_redirect_url: 'redirect_url',
  gotowebinar_webinar_key: 'gotowebinar_webinar_key'
});
const FORM_WIDGET_LEGACY_PARAMS = Object.keys(FORM_WIDGET_PARAMS_MAPPING);

function getFlooredPercentage(width, total) {
  return (width / total * 100 | 0) / 100;
}

function getWidthFromPercentage(percentWidth, total) {
  return percentWidth * total | 0;
}

function indexOfLargest(arr) {
  if (arr.length === 0) {
    return -1;
  }

  if (arr.length === 1) {
    return 0;
  }

  let index = 0;
  let largest = arr[0];

  for (let idx = 1; idx < arr.length; idx++) {
    const w = arr[idx];

    if (w > largest) {
      largest = w;
      index = idx;
    }
  }

  return index;
}
/**
 * Scales widget widths to fill a new total width.
 *
 * @param {Array.<uint>}  widgetWidths    Current widget widths.
 * @param {uint}          oldTotalWidth   Current width widgets fill.
 * @param {uint}          newTotalWidth   New width widgets must fill.
 * @return {Array.<uint>} Array of new widget widths.
 */


export function getScaledWidgetWidthsForNewTotalWidth(widgetWidths, oldTotalWidth, newTotalWidth) {
  const newWidths = [];

  if (!widgetWidths.length) {
    return newWidths;
  } // Check to see if sum of widgetWidths is already equal to newTotalWidth.


  if (widgetWidths.reduce((sum, w) => sum + w) === newTotalWidth) {
    return widgetWidths.slice();
  }

  widgetWidths.forEach(width => {
    const percent = getFlooredPercentage(width, oldTotalWidth);
    const w = getWidthFromPercentage(percent, newTotalWidth);
    newWidths.push(w === 0 ? 1 : w);
  });

  const getNewTotalFromWidgets = () => {
    return newWidths.reduce((total, width) => {
      return total + width;
    }, 0);
  };

  for (let total = getNewTotalFromWidgets(); total !== newTotalWidth; total = getNewTotalFromWidgets()) {
    const index = indexOfLargest(newWidths);
    const increment = total < newTotalWidth ? 1 : -1;
    newWidths[index] += increment;
  }

  return newWidths;
}
/**
 * Sync legacy gallery/image_slider slide params
 *
 * @param {Object} edits  New widget params.
 * @return {Object}       Synced slide params.
 */

export function getSyncedImageSliderWidgetParams(edits) {
  return Object.assign({}, edits, {
    slides: edits.slides.map(slide => {
      if (slide.img && isPlainObject(slide.img)) {
        return Object.assign({}, slide, {}, IMAGE_SLIDER_LEGACY_PARAMS.reduce((hash, oldKey) => {
          const newKey = IMAGE_SLIDER_PARAMS_MAPPING[oldKey];
          hash[oldKey] = slide.img[newKey];
          return hash;
        }, {}));
      }

      return slide;
    })
  });
}
/**
 * Sync legacy image widget params with v2 image widget params.
 *
 * @param {String} widgetType  Legacy widget type.
 * @param {Object} edits       New widget params.
 * @return {Object}            Synced image widget params.
 */

export function getSyncedImageWidgetParams(widget = {}, edits) {
  const {
    params = {},
    type
  } = widget;

  if (isLogoType(widget) && (params.link !== edits.link || params.open_in_new_tab !== edits.open_in_new_tab)) {
    edits.img_link = {
      no_follow: false,
      open_in_new_tab: edits.open_in_new_tab,
      url: {
        type: 'EXTERNAL',
        href: edits.link,
        content_id: null
      }
    };
  } else if (params.link !== edits.link || params.target !== edits.target) {
    edits.img_link = {
      no_follow: false,
      open_in_new_tab: edits.target,
      url: {
        type: 'EXTERNAL',
        href: edits.link,
        content_id: null
      }
    };
  }

  if (!isLegacyImageType(type)) {
    return edits;
  }

  let pickParams;

  if (type === 'logo') {
    pickParams = getSyncedImageWidgetParams.pickLogoParams || (getSyncedImageWidgetParams.pickLogoParams = createOptimizedPick(LOGO_IMAGE_WIDGET_PARAMS));
  } else {
    pickParams = getSyncedImageWidgetParams.pickImageParams || (getSyncedImageWidgetParams.pickImageParams = createOptimizedPick(IMAGE_WIDGET_PARAMS));
  }

  if (edits.img && isPlainObject(edits.img)) {
    return Object.assign({}, edits, {}, pickParams(edits.img));
  }

  return edits;
}
/**
 * Sync legacy form widget params with v2 form widget params.
 *
 * @param {Object} edits   New widget params.
 * @return {Object}        Synced form widget params.
 */

export function getSyncedFormWidgetParams(edits) {
  if (edits.form && isPlainObject(edits.form)) {
    return Object.assign({}, edits, {}, FORM_WIDGET_LEGACY_PARAMS.reduce((hash, oldKey) => {
      const newKey = FORM_WIDGET_PARAMS_MAPPING[oldKey];
      hash[oldKey] = edits.form[newKey];
      return hash;
    }, {}));
  }

  return edits;
}
/**
 * Return proper size type information for portals ungated for responsive images
 *
 * @param {Object} img   Image field values
 * @return {Object}      Updated image field values
 */

export function getResponsiveImageSizeType(img = {}) {
  return Object.assign({
    size_type: ImageSizeTypes.AUTO
  }, img);
}
/**
 * Return initial template-level widget param data for a default module
 *
 * @param {Object} customWidget   Catalog widget
 * @return {Object}      Initial widget params
 */

export function getInitialWidgetParams(customWidget = {}, options = {}) {
  const {
    path,
    fields
  } = customWidget;
  const params = {}; // Set default image size_type value to 'auto' for new image modules

  if (path === '@hubspot/linked_image' && !options.isEmail) {
    const imageField = fields.find(_field => _field.name === 'img') || {};
    params.img = getResponsiveImageSizeType(imageField.default);
  }

  return params;
}